import { useEffect, useRef } from 'react';

export interface UseDebouncedAutosaveOptions<TValue> {
  /** Current form/working value. */
  readonly value: TValue;
  /** Debounce window in milliseconds before save fires. */
  readonly delayMs: number;
  /**
   * Returns true when `value` is unchanged from the last persisted state.
   * The hook will not schedule a save when this returns true.
   */
  readonly isEqual: (a: TValue, b: TValue) => boolean;
  /**
   * Persist `value`. Called after the debounce window elapses with the
   * latest value. The promise/callback contract is intentionally loose —
   * callers can wire to a React Query mutation with onSuccess/onError.
   */
  readonly onSave: (value: TValue) => void;
  /**
   * Latest persisted snapshot. The hook compares against this to decide
   * whether `value` is dirty. Caller must update this (e.g. inside
   * onSuccess of the underlying mutation) so the next compare is fresh.
   */
  readonly persistedSnapshot: TValue;
}

/**
 * Debounced "form is dirty" autosave hook.
 *
 * Compares `value` to `persistedSnapshot`; when they differ, schedules an
 * `onSave(value)` after `delayMs`. New edits inside the window restart the
 * timer (coalescing rapid changes into a single save).
 *
 * The hook is deliberately persistence-agnostic — the caller decides what
 * happens inside `onSave`. Pair with a React Query mutation and update
 * `persistedSnapshot` from the mutation's `onSuccess` to anchor the next
 * dirty check.
 *
 * @example
 *   const persistedRef = useRef(initialState);
 *   useEffect(() => { persistedRef.current = initialState; }, [initialState]);
 *
 *   useDebouncedAutosave({
 *     value: form,
 *     delayMs: 600,
 *     isEqual: formStatesEqual,
 *     persistedSnapshot: persistedRef.current,
 *     onSave: (next) => {
 *       mutation.mutate(buildInput(next), {
 *         onSuccess: () => { persistedRef.current = next; },
 *       });
 *     },
 *   });
 */
export function useDebouncedAutosave<TValue>({
  value,
  delayMs,
  isEqual,
  onSave,
  persistedSnapshot,
}: UseDebouncedAutosaveOptions<TValue>): void {
  // Hold latest callbacks/snapshot in refs so we don't restart the timer
  // every time the parent re-renders with a fresh function identity.
  const onSaveRef = useRef(onSave);
  const isEqualRef = useRef(isEqual);
  const snapshotRef = useRef(persistedSnapshot);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);
  useEffect(() => {
    isEqualRef.current = isEqual;
  }, [isEqual]);
  useEffect(() => {
    snapshotRef.current = persistedSnapshot;
  }, [persistedSnapshot]);

  useEffect(() => {
    if (isEqualRef.current(value, snapshotRef.current)) return;
    const handle = setTimeout(() => {
      onSaveRef.current(value);
    }, delayMs);
    return () => clearTimeout(handle);
  }, [value, delayMs]);
}
